/**
 * UploadProcessingView
 *
 * Displays real-time processing progress for uploaded documents.
 * Shown in the upload sheet after files finish uploading.
 * Reads file list from zustand store + subscribes to processing status via Convex.
 */

import { useEffect, useRef } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useDocumentProcessingStatus } from "@/hooks/use-document-processing-status";
import { useChecklistSuggestions } from "@/hooks/use-checklist-suggestions";
import { useTranslation } from "@/packages/utils";
import { FileIcon } from "../FileIcon";
import {
  ProcessingStepList,
  type ProcessingStep,
  type ProcessingStepStatus,
} from "./ProcessingStepList";
import { ChecklistSuggestions } from "./ChecklistSuggestions";
import { UnmatchedDocumentSection } from "./UnmatchedDocumentSection";
import type { Id } from "@/packages/types";
import { useUploadSessionStore } from "@/stores/upload-session-store";

interface ProcessingFileCardProps {
  file: {
    localId: string;
    fileName: string;
    fileType: string;
    documentId?: Id<"documents">;
    uploadStatus: "pending" | "uploading" | "done" | "error";
  };
  processingStatus:
    | {
        summaryStatus?: ProcessingStepStatus;
        suggestedChecklistItemsStatus?: ProcessingStepStatus;
        isProcessing: boolean;
      }
    | undefined;
  hasAudit: boolean;
  auditId: Id<"audits"> | null;
}

function ProcessingFileCard({
  file,
  processingStatus,
  hasAudit,
  auditId,
}: ProcessingFileCardProps) {
  const { t } = useTranslation(["documents"]);
  const { suggestions } = useChecklistSuggestions(file.documentId ?? null);

  // Handle files that failed to upload
  if (file.uploadStatus === "error") {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
        <div className="flex items-center gap-3">
          <FileIcon type={file.fileType} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-primary truncate">{file.fileName}</p>
            <p className="text-xs text-red-400">
              {t("documents:upload.processing.uploadFailed")}
            </p>
          </div>
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
        </div>
      </div>
    );
  }

  const steps: ProcessingStep[] = [
    {
      label: t("documents:upload.processing.fileUploaded"),
      status: "completed",
    },
    {
      label: t("documents:upload.processing.summaryGenerated"),
      status: processingStatus?.summaryStatus ?? "pending",
    },
    ...(hasAudit
      ? [
          {
            label: t("documents:upload.processing.findingMatches"),
            status:
              processingStatus?.suggestedChecklistItemsStatus ?? "pending",
          },
        ]
      : []),
  ];

  const matchingCompleted =
    hasAudit && processingStatus?.suggestedChecklistItemsStatus === "completed";

  const showSuggestions =
    matchingCompleted && suggestions.length > 0 && file.documentId;

  const showUnmatched =
    matchingCompleted && suggestions.length === 0 && file.documentId && auditId;

  const hasFailedSteps =
    processingStatus?.summaryStatus === "failed" ||
    processingStatus?.suggestedChecklistItemsStatus === "failed";

  return (
    <div
      className={`rounded-lg border p-3 ${
        hasFailedSteps
          ? "border-red-500/30 bg-red-500/5"
          : "border-border bg-card"
      }`}
    >
      <div className="flex items-center gap-3">
        <FileIcon type={file.fileType} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-primary truncate">{file.fileName}</p>
          <p className="text-xs text-muted">{file.fileType}</p>
        </div>
        {hasFailedSteps ? (
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
        ) : processingStatus && !processingStatus.isProcessing ? (
          <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
        ) : (
          <Loader2 size={16} className="text-blue-500 animate-spin shrink-0" />
        )}
      </div>
      <div className="mt-2">
        <ProcessingStepList steps={steps} />
      </div>
      {showSuggestions && file.documentId && (
        <div className="mt-3 pt-3 border-t border-border">
          <ChecklistSuggestions documentId={file.documentId} />
        </div>
      )}
      {showUnmatched && file.documentId && auditId && (
        <div className="mt-3 pt-3 border-t border-border">
          <UnmatchedDocumentSection
            documentId={file.documentId}
            auditId={auditId}
          />
        </div>
      )}
    </div>
  );
}

export function UploadProcessingView() {
  const { t } = useTranslation(["documents"]);
  const sessionFiles = useUploadSessionStore((s) => s.files);
  const selectedAuditIds = useUploadSessionStore((s) => s.selectedAuditIds);
  const hasAudit = selectedAuditIds.length > 0;

  const documentIds = sessionFiles
    .filter(
      (f): f is typeof f & { documentId: Id<"documents"> } =>
        f.documentId !== undefined,
    )
    .map((f) => f.documentId);

  const { statuses } = useDocumentProcessingStatus(documentIds);

  const totalFiles = sessionFiles.length;
  const processedFiles = documentIds.filter((id) => {
    const s = statuses.get(id);
    return s && !s.isProcessing;
  }).length;

  const failedFiles = documentIds.filter((id) => {
    const s = statuses.get(id);
    return (
      s?.summaryStatus === "failed" ||
      s?.suggestedChecklistItemsStatus === "failed"
    );
  }).length;

  const allProcessed = processedFiles === totalFiles && totalFiles > 0;

  // Track previous allProcessed state to detect transition
  const prevAllProcessedRef = useRef(false);
  // Capture the phase when component first mounts to avoid re-firing the toast
  // when the sheet is reopened after processing already completed
  const phaseOnMountRef = useRef<string | null>(null);
  useEffect(() => {
    if (phaseOnMountRef.current === null) {
      phaseOnMountRef.current = useUploadSessionStore.getState().phase;
    }

    if (allProcessed && !prevAllProcessedRef.current) {
      // Only fire toast when processing completes during this component lifecycle,
      // not when the component remounts with processing already done
      if (phaseOnMountRef.current !== "completed") {
        const filesWithSuggestions = documentIds.filter((id) => {
          const s = statuses.get(id);
          return s?.suggestedChecklistItemsStatus === "completed";
        });

        if (filesWithSuggestions.length > 0) {
          toast.success(
            t("documents:upload.processing.complete", {
              count: filesWithSuggestions.length,
            }),
            { richColors: true },
          );
        } else {
          toast.success(t("documents:upload.processing.allProcessed"), {
            richColors: true,
          });
        }
      }

      // Transition session phase to completed
      useUploadSessionStore.getState().setPhase("completed");
    }
    prevAllProcessedRef.current = allProcessed;
  }, [allProcessed, documentIds, statuses, t]);

  return (
    <motion.div
      className="flex flex-col gap-3"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Overall progress header */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-secondary">
          {allProcessed && failedFiles > 0
            ? t("documents:upload.processing.completedWithErrors", {
                count: failedFiles,
              })
            : allProcessed
              ? t("documents:upload.processing.allFilesProcessed")
              : t("documents:upload.processing.inProgress", {
                  count: totalFiles,
                })}
        </span>
        <AnimatePresence mode="wait">
          {!allProcessed && (
            <motion.div
              key="loading"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <Loader2 size={14} className="text-blue-500 animate-spin" />
            </motion.div>
          )}
          {allProcessed && failedFiles > 0 && (
            <motion.div
              key="error"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <AlertTriangle size={14} className="text-red-500" />
            </motion.div>
          )}
          {allProcessed && failedFiles === 0 && (
            <motion.div
              key="success"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <CheckCircle2 size={14} className="text-emerald-500" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Per-file processing cards */}
      <div className="space-y-2">
        {sessionFiles.map((file, index) => (
          <motion.div
            key={file.localId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: index * 0.08 }}
          >
            <ProcessingFileCard
              file={file}
              processingStatus={
                file.documentId ? statuses.get(file.documentId) : undefined
              }
              hasAudit={hasAudit}
              auditId={selectedAuditIds[0] ?? null}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
